import React from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Grid,
  FormControl,
  Divider,
  Backdrop,
  TextField,
} from '@mui/material';
import HttpService from '../../hooks/Https-services';
import { API_ROUTES } from '../../utils/constant';
import { DateRangePicker } from 'rsuite';
import { LoadingButton } from '@mui/lab';
import { downloadReportByType } from './reportDownload';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

type Period = {
  token: string;
  periodNo: number;
  taggerName: string | null;
  taggingEnd: string | null;
  taggingStart: string | null;
  taggingDuration: number | null;
  previousTaggerName: string | null;
};

type GameItem = {
  gameId: number;
  fetchedAt: string | null;
  assignedAt: string | null;
  priority: string;
  status: string;
  completedAt: string | null;
  moderatorName: string | null;
  moderatorTeam: string | null;
  qcModeratorName: string | null;
  previousModeratorName: string | null;
  gameReassigneeName: string | null;
  gameAssignmentType: string | null;
  gameAssigneeName: string | null;
  periods: Period[];
};

function formatDateOnly(d: Date) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function parseBackendDate(val: any): Date | null {
  if (val === undefined || val === null) return null;
  let s = String(val).trim();
  if (!s) return null;
  // Common backend format: 'YYYY-MM-DD HH:mm:ss.SSSSSS'
  // Normalize to ISO-like first
  let d = new Date(s.replace(' ', 'T'));
  if (!isNaN(d.getTime())) return d;
  // Fallback: take only date part 'YYYY-MM-DD'
  const part = s.slice(0, 10);
  const [y, m, da] = part.split('-').map((n) => Number(n));
  if (y && m && da) {
    d = new Date(y, (m || 1) - 1, da || 1);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

export default function RealTimeReportPage() {
  const { allowedMaxDays, afterToday, combine } = DateRangePicker;
  const [items, setItems] = React.useState<GameItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const getTodayRange = React.useCallback((): [Date, Date] => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  }, []);

  const [dateRange, setDateRange] = React.useState<[Date, Date] | null>(() =>
    getTodayRange()
  );
  const [downloading, setDownloading] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>('');

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build start/end using picker if selected, else fallback to today's UTC bounds
      const now = new Date();
      const startUtc =
        dateRange && dateRange[0] ? new Date(dateRange[0]) : new Date(now);
      startUtc.setHours(0, 0, 0, 0);
      const endUtc =
        dateRange && dateRange[1] ? new Date(dateRange[1]) : new Date(now);
      endUtc.setHours(23, 59, 59, 999);

      const params: any = {
        type: 2,
        startDate: startUtc.toISOString(), // T00:00:00.000Z
        endDate: endUtc.toISOString(), // T23:59:59.999Z
      };

      params.teamIds = String(3);

      const res = await HttpService.get(
        `${API_ROUTES.report}?${new URLSearchParams(params).toString()}`
      );
      const payload = res?.data;
      const data: GameItem[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setItems(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const taggerDateAgg = React.useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    const dateSet = new Set<string>();
    for (const game of items) {
      const periods: any[] = (game as any)?.periods ?? [];
      for (const p of periods) {
        const end = p?.taggingEnd;
        if (end === undefined || end === null || String(end) === '') continue;
        const d = parseBackendDate(end);
        if (!d) continue;
        const dateKey = formatDateOnly(d);
        const tagger = String(p?.taggerName ?? '');
        if (!map[tagger]) map[tagger] = {};
        map[tagger][dateKey] = (map[tagger][dateKey] || 0) + 1;
        dateSet.add(dateKey);
      }
    }
    const dates = Array.from(dateSet).sort((a, b) => {
      const [ad, am, ay] = a.split('/').map(Number);
      const [bd, bm, by] = b.split('/').map(Number);
      const aT = new Date(ay, (am || 1) - 1, ad || 1).getTime();
      const bT = new Date(by, (bm || 1) - 1, bd || 1).getTime();
      return aT - bT;
    });
    const taggers = Object.keys(map);
    return { map, dates, taggers };
  }, [items]);

  const taggersFiltered = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = Array.isArray(taggerDateAgg.taggers)
      ? taggerDateAgg.taggers
      : [];
    if (!s) return base;
    return base.filter((t) => t.toLowerCase().includes(s));
  }, [taggerDateAgg.taggers, search]);

  const stackedSeries = React.useMemo(() => {
    const ds = Array.isArray(taggerDateAgg.dates) ? taggerDateAgg.dates : [];
    const tg = Array.isArray(taggersFiltered) ? taggersFiltered : [];
    return ds.map((date) => ({
      name: date,
      data: tg.map((t) => taggerDateAgg.map[t]?.[date] || 0),
    }));
  }, [taggerDateAgg, taggersFiltered]);

  const totalsPerTagger = React.useMemo(() => {
    const totals: Record<string, number> = {};
    const tg = Array.isArray(taggersFiltered) ? taggersFiltered : [];
    const ds = Array.isArray(taggerDateAgg.dates) ? taggerDateAgg.dates : [];
    for (const t of tg) {
      let sum = 0;
      for (const d of ds) sum += taggerDateAgg.map[t]?.[d] || 0;
      totals[t] = sum;
    }
    return totals;
  }, [taggerDateAgg, taggersFiltered]);

  const chartOptions: ApexOptions = React.useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: { show: true },
        animations: { enabled: false },
      },
      plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => (val && val > 0 ? String(val) : ''),
        style: { fontSize: '10px' },
      },
      legend: { position: 'top' },
      xaxis: {
        categories: Array.isArray(taggersFiltered) ? taggersFiltered : [],
        title: { text: 'Tagger Name' },
        labels: { rotate: -45, hideOverlappingLabels: true },
      },
      yaxis: { title: { text: 'Total Periods Completed' } },
      grid: { strokeDashArray: 4 },
      tooltip: { shared: true, intersect: false },
      annotations: {
        points: (Array.isArray(taggersFiltered) ? taggersFiltered : []).map(
          (t) => ({
            x: t,
            y: totalsPerTagger[t] || 0,
            marker: { size: 0 },
            label: { text: String(totalsPerTagger[t] || 0), offsetY: -6 },
          })
        ),
      },
    }),
    [taggersFiltered, totalsPerTagger]
  );

  const chartWidth = React.useMemo(() => {
    const perItem = 50;
    const count = Array.isArray(taggersFiltered) ? taggersFiltered.length : 0;
    return Math.max(800, count * perItem);
  }, [taggersFiltered]);

  return (
    <>
      <Divider sx={{ borderStyle: 'dashed', mb: 2, mt: 2 }} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        mt={4}
      >
        <Typography variant="h5">Real-Time Report</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <LoadingButton
            variant="outlined"
            loading={loading}
            onClick={() => {
              fetchData();
            }}
            startIcon={<RefreshIcon />}
          >
            Refresh Data
          </LoadingButton>
          <LoadingButton
            variant="contained"
            loading={downloading}
            onClick={() => {
              setDownloading(true);
              try {
                downloadReportByType(2, items, 'real_time_report');
              } finally {
                setDownloading(false);
              }
            }}
            startIcon={<DownloadIcon />}
          >
            Download Report
          </LoadingButton>
        </Stack>
      </Stack>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Date Range
        </Typography>
        <FormControl
          fullWidth
          //   sx={{
          //     '& .rs-picker-toggle': {
          //       borderColor: dateError ? 'error.main' : undefined,
          //     },
          //   }}
          sx={{ mb: 2 }}
        >
          <DateRangePicker
            character=" – "
            size="lg"
            shouldDisableDate={combine(allowedMaxDays(31), afterToday())}
            onChange={(val) => setDateRange((val as any) || getTodayRange())}
            onClean={() => setDateRange(getTodayRange())}
            value={dateRange as any}
            showMeridiem
            format="dd/MM/yyyy hh:mm aa"
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Search by Tagger Name
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Type to filter tagger names"
          sx={{ mb: 2 }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Completed Periods by Tagger
        </Typography>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: `${chartWidth}px` }}>
            {(() => {
              const tg = Array.isArray(taggersFiltered) ? taggersFiltered : [];
              const seriesSafe =
                Array.isArray(stackedSeries) && stackedSeries.length > 0
                  ? stackedSeries
                  : [{ name: 'No Data', data: tg.map(() => 0) }];
              return (
                <ReactApexChart
                  options={chartOptions}
                  series={seriesSafe as any}
                  type="bar"
                  height={420}
                />
              );
            })()}
          </Box>
        </Box>
      </Grid>

      {error && (
        <Box display="flex" justifyContent="center" py={3}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
